/**
 * Item context menu — open/close state (2026-08-09).
 *
 * WHY A STORE AND NOT LOCAL COMPONENT STATE. Two surfaces open this menu and
 * neither can see the other: `EditableItem`'s `contextmenu` (mouse) and
 * `App`'s two-finger-tap handler (iPad, which resolves the item via
 * `elementFromPoint` and so never touches the component). A module signal is
 * the only place both can write, and it also guarantees what a per-item
 * local signal could not: exactly ONE menu open at a time.
 *
 * The position is in VIEWPORT coordinates because the menu is body-portalled
 * and `position: fixed` — the same rect-anchored-popover exemption to DEC-096
 * that the canvas right-click menu and ShapePicker already take. `canvas`
 * holds the same point converted to canvas design-space, which is what Paste
 * needs to drop a copy where the operator actually clicked.
 */

import { createSignal, type Accessor } from 'solid-js';

export interface ItemMenuState {
  itemId: string;
  /** Viewport px — where to render the menu. */
  x: number;
  y: number;
  /** Canvas design-space px of the same point, or null when the click could
   *  not be resolved against `.scaled-canvas-inner` (menu opened before the
   *  canvas mounted). Paste falls back to cascading off the source item. */
  canvas: { x: number; y: number } | null;
}

const [menuSig, setMenuSig] = createSignal<ItemMenuState | null>(null);

/** The open item menu, or null. */
export const itemMenu: Accessor<ItemMenuState | null> = menuSig;

/**
 * Convert a viewport point to canvas design-space, undoing `.shell`'s
 * `transform: scale()`. Returns null when the canvas isn't in the DOM.
 * Exported because the two-finger path needs the same conversion.
 */
export function viewportToCanvas(x: number, y: number): { x: number; y: number } | null {
  if (typeof document === 'undefined') return null;
  const el = document.querySelector('.scaled-canvas-inner') as HTMLElement | null;
  if (!el) return null;
  const rect = el.getBoundingClientRect();
  if (el.clientWidth <= 0 || rect.width <= 0) return null;
  const scale = rect.width / el.clientWidth;
  return { x: (x - rect.left) / scale, y: (y - rect.top) / scale };
}

export function openItemMenu(itemId: string, x: number, y: number): void {
  setMenuSig({ itemId, x, y, canvas: viewportToCanvas(x, y) });
}

export function closeItemMenu(): void { setMenuSig(null); }
